import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, RefreshCw, Truck, Calendar, Filter, X } from 'lucide-react';
import '../styles/MasterForms.css';

const DRSList = () => {
  const [drsList, setDRSList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    employeeid: '',
    vehicleid: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDRS, setSelectedDRS] = useState(null);
  
  // Dropdown data
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    loadDropdownData();
    loadDRSList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterDRSList();
  }, [filterDRSList]);

  // ============================================
  // LOAD DROPDOWN DATA
  // ============================================
  const loadDropdownData = async () => {
    try {
      const empResponse = await fetch('/api/employees.php?companyid=1&isactive=1');
      const empData = await empResponse.json();
      if (empData.success) {
        setEmployees(empData.data || []);
      }

      const vehResponse = await fetch('/api/vehicles.php?companyid=1&isactive=1');
      const vehData = await vehResponse.json();
      if (vehData.success) {
        setVehicles(vehData.data || []);
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  // ============================================
  // LOAD DRS LIST
  // ============================================
  const loadDRSList = async () => {
    try {
      setLoading(true);
      
      let url = '/api/drs.php?companyid=1';
      
      if (filters.fromDate) url += `&fromdate=${filters.fromDate}`;
      if (filters.toDate) url += `&todate=${filters.toDate}`;
      if (filters.employeeid) url += `&employeeid=${filters.employeeid}`;
      if (filters.vehicleid) url += `&vehicleid=${filters.vehicleid}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setDRSList(data.data || []);
      }
    } catch (error) {
      console.error('Error loading DRS list:', error);
      alert('Failed to load DRS list');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTER DRS LIST
  // ============================================
  const filterDRSList = useCallback(() => {
    let filtered = [...drsList];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(drs => 
        drs.drsno?.toLowerCase().includes(search) ||
        drs.deliveredby_name?.toLowerCase().includes(search) ||
        drs.vehicle_regno?.toLowerCase().includes(search)
      );
    }
    
    setFilteredList(filtered);
  }, [drsList, searchTerm]);

  // ============================================
  // APPLY FILTERS
  // ============================================
  const handleApplyFilters = () => {
    loadDRSList();
    setShowFilters(false);
  };

  // ============================================
  // CLEAR FILTERS
  // ============================================
  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      employeeid: '',
      vehicleid: ''
    });
    setSearchTerm('');
    loadDRSList();
  };

  // ============================================
  // VIEW DRS DETAILS
  // ============================================
  const handleViewDetails = async (drsId) => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/drs.php?id=${drsId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedDRS(data.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Error loading DRS details:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // DELETE DRS
  // ============================================
  const handleDelete = async (drsId, drsNo) => {
    if (!window.confirm(`Are you sure you want to delete DRS ${drsNo}?\n\nThis will also delete all detail lines.`)) {
      return;
    }

    try {
      const response = await fetch('/api/drs.php', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drsid: drsId })
      });

      const data = await response.json();

      if (data.success) {
        alert('DRS deleted successfully');
        loadDRSList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting DRS:', error);
      alert('Failed to delete DRS');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const handleNewDRS = () => {
    window.location.href = '/drs-entry';
  };

  const handleEditDRS = (drsId) => {
    window.location.href = `/drs-entry?id=${drsId}`;
  };

  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Daily Run Sheet (DRS) List</h2>
          <p className="subtitle">{filteredList.length} DRS records</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={20} /> Filters
          </button>
          <button className="btn-primary" onClick={handleNewDRS}>
            <Plus size={20} /> New DRS
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0 }}>Filters</h3>
            <button className="btn-icon" onClick={() => setShowFilters(false)}><X size={20} /></button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '15px' }}>
            <div className="form-group">
              <label>From Date</label>
              <input type="date" value={filters.fromDate} onChange={(e) => setFilters({...filters, fromDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input type="date" value={filters.toDate} onChange={(e) => setFilters({...filters, toDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Driver/Employee</label>
              <select value={filters.employeeid} onChange={(e) => setFilters({...filters, employeeid: e.target.value})}>
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.employeeid} value={emp.employeeid}>{emp.employeename}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Vehicle</label>
              <select value={filters.vehicleid} onChange={(e) => setFilters({...filters, vehicleid: e.target.value})}>
                <option value="">All Vehicles</option>
                {vehicles.map(veh => (
                  <option key={veh.VehicleID} value={veh.VehicleID}>{veh.RegistrationNo}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={handleClearFilters}><X size={18} /> Clear</button>
            <button className="btn-primary" onClick={handleApplyFilters}><Filter size={18} /> Apply</button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div style={{ background: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search by DRS No, Driver, or Vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', paddingLeft: '40px', height: '42px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            )}
          </div>
          <button className="btn-secondary" onClick={loadDRSList}><RefreshCw size={18} /> Refresh</button>
        </div>
      </div>

      {/* DRS Table */}
      <div style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: '20px' }}>Loading...</p>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="empty-state">
            <Truck size={64} style={{ color: '#d1d5db', marginBottom: '20px' }} />
            <h3>No DRS Records</h3>
            <p>Create your first DRS to get started</p>
            <button className="btn-primary" onClick={handleNewDRS}><Plus size={20} /> Create DRS</button>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>DRS No</th>
                  <th>DRS Date</th>
                  <th>Despatch Date</th>
                  <th>Driver</th>
                  <th>Vehicle</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((drs) => (
                  <tr key={drs.drsid}>
                    <td className="font-mono" style={{ fontWeight: 'bold', color: '#3b82f6' }}>{drs.drsno}</td>
                    <td>{formatDate(drs.drsdate)}</td>
                    <td>{formatDate(drs.despatchdate)}</td>
                    <td>{drs.deliveredby_name || '-'}</td>
                    <td><Truck size={16} style={{ display: 'inline', marginRight: '6px', color: '#10b981' }} />{drs.vehicle_regno || '-'}</td>
                    <td className="text-center"><span style={{ background: '#f3f4f6', padding: '4px 12px', borderRadius: '12px', fontWeight: '600' }}>{drs.total_items || 0}</span></td>
                    <td className="text-right" style={{ fontWeight: 'bold', color: '#10b981' }}>{formatAmount(drs.totalamount)} SAR</td>
                    <td className="actions-column">
                      <button className="btn-icon btn-view" onClick={() => handleViewDetails(drs.drsid)} title="View"><Eye size={16} /></button>
                      <button className="btn-icon btn-edit" onClick={() => handleEditDRS(drs.drsid)} title="Edit"><Edit2 size={16} /></button>
                      <button className="btn-icon btn-delete" onClick={() => handleDelete(drs.drsid, drs.drsno)} title="Delete"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb', fontWeight: 'bold' }}>
                  <td colSpan="5" style={{ textAlign: 'right' }}>Total:</td>
                  <td className="text-center">{filteredList.reduce((sum, drs) => sum + (parseInt(drs.total_items) || 0), 0)}</td>
                  <td className="text-right" style={{ color: '#10b981', fontSize: '16px' }}>{formatAmount(filteredList.reduce((sum, drs) => sum + (parseFloat(drs.totalamount) || 0), 0))} SAR</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* DRS Details Modal */}
      {showDetailsModal && selectedDRS && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <div>
                <h3>DRS Details - {selectedDRS.drsno}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '5px 0 0 0' }}>{formatDate(selectedDRS.drsdate)}</p>
              </div>
              <button onClick={() => setShowDetailsModal(false)}><X size={20} /></button>
            </div>
            
            <div className="modal-body">
              <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '5px' }}>Driver</label>
                    <div style={{ fontWeight: '600' }}>{selectedDRS.deliveredby_name || '-'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '5px' }}>Vehicle</label>
                    <div style={{ fontWeight: '600' }}>{selectedDRS.vehicle_regno || '-'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '5px' }}>Total</label>
                    <div style={{ fontWeight: '600', color: '#10b981', fontSize: '18px' }}>{formatAmount(selectedDRS.totalamount)} SAR</div>
                  </div>
                </div>
              </div>

              <h4>Detail Lines ({selectedDRS.details?.length || 0})</h4>
              
              {selectedDRS.details && selectedDRS.details.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>AWB</th>
                      <th>CC</th>
                      <th>MC</th>
                      <th>CC Rcvd</th>
                      <th>MC Rcvd</th>
                      <th>Total</th>
                      <th>Receiver</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDRS.details.map((d, i) => (
                      <tr key={i}>
                        <td className="font-mono">{d.awbno}</td>
                        <td className="text-right">{formatAmount(d.couriercharge)}</td>
                        <td className="text-right">{formatAmount(d.materialcost)}</td>
                        <td className="text-right" style={{ fontWeight: 'bold', color: '#10b981' }}>{formatAmount(d.cc_received)}</td>
                        <td className="text-right" style={{ fontWeight: 'bold', color: '#10b981' }}>{formatAmount(d.mc_received)}</td>
                        <td className="text-right" style={{ fontWeight: 'bold', color: '#3b82f6' }}>{formatAmount(parseFloat(d.cc_received) + parseFloat(d.mc_received))}</td>
                        <td>{d.receivername || '-'}</td>
                        <td><span className="status-badge active">{d.deliverystatus || 'Pending'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No details</div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
              <button className="btn-primary" onClick={() => handleEditDRS(selectedDRS.drsid)}><Edit2 size={18} /> Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DRSList;